import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import axiosRetry from 'axios-retry';
import { getToken, isTokenValid } from 'src/hooks';
import { AuthService } from 'src/services/AuthService';

import { CCBE_API_URL, SB_API_URL } from './environment';
import { handleErrors } from './errors';

export const clientSB = axios.create({
  baseURL: SB_API_URL,
});
export const clientCC = axios.create({
  baseURL: CCBE_API_URL(),
});

const requestInterceptor = (request: InternalAxiosRequestConfig) => {
  request.headers.Authorization = `Bearer ${getToken()}`;

  return request;
};

export const retryCondition = (error: AxiosError) => {
  switch (error.response?.status) {
    case 401: {
      return true;
    }

    default: {
      return false;
    }
  }
};

export const onRetry = async () => {
  if (!isTokenValid()) {
    await AuthService.getInstance().refreshToken();
  }
};

const axiosRetryConfig = {
  retryCondition: retryCondition,
  onRetry: onRetry,
};

clientCC.interceptors.request.use(requestInterceptor);
clientSB.interceptors.request.use(requestInterceptor);

axiosRetry(clientCC, axiosRetryConfig);
axiosRetry(clientSB, axiosRetryConfig);

export const requestSBApi = <D = any, R = any>(
  { ...options }: AxiosRequestConfig<D>,
  showSnackbarError?: boolean,
  customErrorMessage?: string,
): Promise<AxiosResponse<R>> => {
  const onSuccess = (response: AxiosResponse<R>) => response;

  return clientSB(options)
    .then(onSuccess)
    .catch((err) => handleErrors(err, showSnackbarError, customErrorMessage));
};

export const requestCCApi = <DataTypes>(
  { ...options }: AxiosRequestConfig<DataTypes>,
  showSnackbarError?: boolean,
  customErrorMessage?: string | ((error: AxiosError) => string),
) => {
  const onSuccess = (response: any) => response.data;

  return clientCC(options)
    .then(onSuccess)
    .catch((err) => {
      let message: string | undefined;
      if (
        customErrorMessage &&
        typeof customErrorMessage === 'function' &&
        axios.isAxiosError(err)
      ) {
        message = customErrorMessage(err);
      } else if (customErrorMessage && typeof customErrorMessage === 'string') {
        message = customErrorMessage;
      }
      handleErrors(err, showSnackbarError, message);
    });
};
